// R69 (docs/17-r69-reconciliation.md): is a record on a tree the chain has
// left proven applied by the transaction its ledger names?
//
// One implication, in one direction only (docs/17 2.2): if the transaction X
// whose signature the record saved is finalized and successful (E1, E2), and
// X carries the record's own authorization at top level, addressed to this
// program and this vault (2.4), then the authorization was applied. Nothing
// is inferred the other way: a failed or missing X says nothing about it.
//
// `topLevelExecutes` and `appliedBy` are pure. `EarlierReads` (R77) is the
// session's evidence reader: one pass at a time, one cap per pass, and a memo
// of final answers only (docs/17 section 6). It reads no ledger and writes
// nothing; the signer's `resolveEarlier` judges what it returns.

import { getBase58Encoder } from '@solana/kit';
import { type AccountMetaSpec, type ExecHeaderSpec, type InnerIx, executeDigest, hex, parseExecute } from './digest.js';
import type { LeafRecord } from './ledger.js';
import type { TransactionJson } from './receipt.js';
import { equalBytes } from './recovery.js';

/** One entry of `getSignatureStatuses` (E1), `searchTransactionHistory: true`; `null` when the endpoint has no record of the signature. */
export interface SignatureStatusJson {
  err: unknown;
  confirmationStatus?: string;
  slot?: number;
}

/**
 * What the reads returned for one record's saved transaction signature:
 * the E1 entry, and the E2 answer (`getTransaction`, `json` encoding,
 * `maxSupportedTransactionVersion: 1`), which is read only after E1 is
 * finalized and successful. `transaction` is absent when E2 was not read and
 * `null` when the endpoint had no record of it.
 */
export interface EarlierEvidence {
  txSignature: string;
  status: SignatureStatusJson | null;
  transaction?: TransactionJson | null;
}

/** The identities the proof binds (docs/17 2.4, 2.6): this program, this vault's `vault_id` (the signer's) and this vault's address. */
export interface EarlierIds {
  programId: Uint8Array;
  vaultId: Uint8Array;
  vaultAddress: Uint8Array;
}

/**
 * Why a record is not proven applied (docs/17 section 3, rows 7-11):
 * `no-status` the E1 entry is null or malformed; `not-final` E1 is
 * `processed` or `confirmed`, with or without `err`; `failed` E1 is
 * `finalized` with `err`; `no-transaction` E2 is null; `disagree` E2 lacks
 * `meta`, carries `meta.err`, or differs from E1 in slot or from the saved
 * signature in `signatures[0]`; `unreadable-transaction` E2 agrees but its
 * body is not readable (R86: `message`, `accountKeys` or `instructions`
 * missing or of the wrong shape), so it is read again rather than judged;
 * `no-matching-execute` no top-level Execute to this program and vault
 * carries the record's signature and digest; `next-key` one does, but its
 * header lacks NEXT_KEY or carries other bytes than `record.nextKey`.
 */
export type NotAppliedReason = 'no-status' | 'failed' | 'not-final' | 'no-transaction' | 'disagree' | 'unreadable-transaction' | 'no-matching-execute' | 'next-key';

export type AppliedResult = { applied: true; landingSlot: bigint } | { applied: false; reason: NotAppliedReason };

/** A top-level instruction of the transaction addressed to this program with this vault as account 0, parsed as an Execute. */
export interface TopLevelExecute {
  /** Its index in `transaction.message.instructions`. */
  index: number;
  signature: Uint8Array;
  header: ExecHeaderSpec;
  aux: AccountMetaSpec[];
  inners: InnerIx[];
}

const base58 = getBase58Encoder();

/** A 32-byte address from base58, or `null` for anything that is not one. */
function addressFrom(value: unknown): Uint8Array | null {
  if (typeof value !== 'string') return null;
  try {
    const bytes = new Uint8Array(base58.encode(value));
    return bytes.length === 32 ? bytes : null;
  } catch {
    return null;
  }
}

/**
 * The top-level instructions of `tx` addressed to `programId` with
 * `vaultAddress` as account 0, each parsed with `parseExecute` (docs/17 2.4).
 * Only `transaction.message.instructions` is read: an entry in
 * `meta.innerInstructions` does not count, and `meta.loadedAddresses` is not
 * consulted. An index that is not an integer inside `accountKeys`, a
 * malformed field, base58 that does not decode, and data that does not parse
 * as an Execute with no trailing bytes or accounts are skipped. Account flags
 * are not part of the digest, so only addresses are taken.
 */
export function topLevelExecutes(tx: TransactionJson, programId: Uint8Array, vaultAddress: Uint8Array): TopLevelExecute[] {
  const message: unknown = (tx as { transaction?: { message?: unknown } } | null | undefined)?.transaction?.message;
  if (!message || typeof message !== 'object') return [];
  const keys: unknown = (message as { accountKeys?: unknown }).accountKeys;
  const instructions: unknown = (message as { instructions?: unknown }).instructions;
  if (!Array.isArray(keys) || !Array.isArray(instructions)) return [];
  const decoded = new Map<number, Uint8Array | null>();
  const key = (index: unknown): Uint8Array | null => {
    if (typeof index !== 'number' || !Number.isInteger(index) || index < 0 || index >= keys.length) return null;
    if (!decoded.has(index)) decoded.set(index, addressFrom(keys[index]));
    return decoded.get(index)!;
  };
  const out: TopLevelExecute[] = [];
  instructions.forEach((ix: unknown, index) => {
    if (!ix || typeof ix !== 'object') return;
    const { programIdIndex, accounts, data } = ix as { programIdIndex?: unknown; accounts?: unknown; data?: unknown };
    const program = key(programIdIndex);
    if (!program || !equalBytes(program, programId)) return;
    if (!Array.isArray(accounts) || accounts.length === 0 || typeof data !== 'string') return;
    const metas: AccountMetaSpec[] = [];
    for (const a of accounts) {
      const pubkey = key(a);
      if (!pubkey) return;
      metas.push({ pubkey, isSigner: false, isWritable: false });
    }
    if (!equalBytes(metas[0]!.pubkey, vaultAddress)) return;
    let bytes: Uint8Array;
    try {
      bytes = new Uint8Array(base58.encode(data));
    } catch {
      return;
    }
    try {
      out.push({ index, ...parseExecute(bytes, metas) });
    } catch {
      return;
    }
  });
  return out;
}

type E1 = { kind: 'success'; slot: number } | { kind: 'failure' } | { kind: 'not-final' } | { kind: 'none' };

/** E1's verdict (docs/17 2.3): `success` needs `finalized`, `err === null` and an integer `slot`; `failure` is `finalized` with a non-null `err`; a null or malformed entry is `none`. */
function judgeE1(status: unknown): E1 {
  if (!status || typeof status !== 'object') return { kind: 'none' };
  const s = status as { err?: unknown; confirmationStatus?: unknown; slot?: unknown };
  if (s.confirmationStatus !== 'finalized') return { kind: 'not-final' };
  if (s.err === undefined) return { kind: 'none' };
  if (s.err !== null) return { kind: 'failure' };
  if (typeof s.slot !== 'number' || !Number.isSafeInteger(s.slot) || s.slot < 0) return { kind: 'none' };
  return { kind: 'success', slot: s.slot };
}

/**
 * Whether an E2 body is readable (R86): `transaction.message` is an object
 * whose `accountKeys` is an array of strings and whose `instructions` is an
 * array of objects, each with an integer `programIdIndex`, an array of
 * integer `accounts` and a string `data`: the shape of the `json` encoding.
 * Shape only. Whether a key decodes or an instruction parses as an Execute
 * is content, which `topLevelExecutes` judges against the record, so a
 * complete body with no matching Execute is a final answer and is memoised;
 * an unreadable body is not final, is never memoised, and is read again.
 */
function readableBody(tx: { transaction?: unknown }): boolean {
  const message: unknown = tx.transaction && typeof tx.transaction === 'object' ? (tx.transaction as { message?: unknown }).message : undefined;
  if (!message || typeof message !== 'object') return false;
  const { accountKeys, instructions } = message as { accountKeys?: unknown; instructions?: unknown };
  if (!Array.isArray(accountKeys) || !accountKeys.every((k: unknown) => typeof k === 'string')) return false;
  if (!Array.isArray(instructions)) return false;
  return instructions.every((ix: unknown) => {
    if (!ix || typeof ix !== 'object') return false;
    const { programIdIndex, accounts, data } = ix as { programIdIndex?: unknown; accounts?: unknown; data?: unknown };
    return Number.isInteger(programIdIndex) && Array.isArray(accounts) && accounts.every((a: unknown) => Number.isInteger(a)) && typeof data === 'string';
  });
}

/**
 * E2's agreement with a successful E1 (docs/17 2.3): non-null, `meta`
 * present, `meta.err === null`, the same slot, and `signatures[0]` the saved
 * signature; `unreadable` when all of that holds but the body fails
 * `readableBody` (R86), which is not final.
 */
function judgeE2(tx: unknown, txSignature: string, slot: number): 'agree' | 'none' | 'disagree' | 'unreadable' {
  if (!tx || typeof tx !== 'object') return 'none';
  const t = tx as { slot?: unknown; meta?: unknown; transaction?: { signatures?: unknown } };
  if (!t.meta || typeof t.meta !== 'object' || (t.meta as { err?: unknown }).err !== null) return 'disagree';
  if (t.slot !== slot) return 'disagree';
  const signatures = t.transaction && typeof t.transaction === 'object' ? t.transaction.signatures : undefined;
  if (!Array.isArray(signatures) || signatures[0] !== txSignature) return 'disagree';
  if (!readableBody(t)) return 'unreadable';
  return 'agree';
}

/**
 * Whether `evidence` proves `record`'s authorization applied (docs/17 2.3,
 * 2.4). E1 must be finalized and successful with a slot S; E2 must agree
 * with it; and at least one top-level Execute to `ids.programId` with
 * `ids.vaultAddress` as account 0 must carry `record.signature` byte for
 * byte, over a digest that, rebuilt at the record's own tree and leaf with
 * this program id, `vault_id` and vault address, equals `record.digest`, and
 * whose header carries NEXT_KEY with bytes equal to `record.nextKey`. The
 * digest binds the header the record signed, but `record.nextKey` is
 * separate ledger metadata, so it is checked explicitly: a record whose
 * `nextKey` disagrees with its own digest is never proven (`next-key`).
 * Pure: reads nothing but its arguments. On success the landing slot is S.
 */
export function appliedBy(record: LeafRecord, evidence: EarlierEvidence, ids: EarlierIds): AppliedResult {
  const e1 = judgeE1(evidence.status);
  if (e1.kind === 'none') return { applied: false, reason: 'no-status' };
  if (e1.kind === 'not-final') return { applied: false, reason: 'not-final' };
  if (e1.kind === 'failure') return { applied: false, reason: 'failed' };
  const e2 = judgeE2(evidence.transaction, evidence.txSignature, e1.slot);
  if (e2 === 'none') return { applied: false, reason: 'no-transaction' };
  if (e2 === 'disagree') return { applied: false, reason: 'disagree' };
  if (e2 === 'unreadable') return { applied: false, reason: 'unreadable-transaction' };
  if (typeof record.signature !== 'string') return { applied: false, reason: 'no-matching-execute' };
  let nextKeyDiffers = false;
  for (const ex of topLevelExecutes(evidence.transaction!, ids.programId, ids.vaultAddress)) {
    if (hex(ex.signature) !== record.signature) continue;
    let digest: Uint8Array;
    try {
      digest = executeDigest({ programId: ids.programId, vaultId: ids.vaultId, vaultAddress: ids.vaultAddress, treeIndex: record.treeIndex, leaf: record.leaf, header: ex.header, aux: ex.aux.map((a) => a.pubkey), inners: ex.inners });
    } catch {
      continue;
    }
    if (hex(digest) !== record.digest) continue;
    if (ex.header.nextKey === undefined || record.nextKey === undefined || hex(ex.header.nextKey) !== record.nextKey) {
      nextKeyDiffers = true;
      continue;
    }
    return { applied: true, landingSlot: BigInt(e1.slot) };
  }
  return { applied: false, reason: nextKeyDiffers ? 'next-key' : 'no-matching-execute' };
}

/** The evidence pass's cap per pass, EARLIER_READ_CAP_MS (docs/17 section 6): a proposed value, to be measured (P5), not a measurement. */
export const EARLIER_READ_CAP_MS = 5_000;

/** One record's saved transaction signature, the unit the reader answers and memoises (R77). */
export interface EarlierPair {
  /** The record's key, `${vaultId}:${tree}:${leaf}`. */
  key: string;
  txSignature: string;
}

export interface EarlierReadsConfig {
  /** E1: one `getSignatureStatuses` call, `searchTransactionHistory: true`, one entry per signature in order. */
  statuses(signatures: string[]): Promise<readonly (SignatureStatusJson | null)[]>;
  /** E2: `getTransaction(signature)`, `json` encoding, `maxSupportedTransactionVersion: 1`; null when not found. */
  transaction(signature: string): Promise<TransactionJson | null>;
  /** The cap each pass races, in milliseconds. */
  capMs: number;
}

interface Pass {
  /** Set when the cap fires: nothing the pass receives afterwards starts a call, is memoised or is returned. */
  dead: boolean;
  result: Promise<Map<string, EarlierEvidence> | 'capped'>;
}

const pairId = (p: EarlierPair): string => `${p.key}\u0000${p.txSignature}`;

/**
 * The session's evidence reader (docs/17 section 6, R77). One instance per
 * engine, so one per unlocked session; its only state is the pass in flight,
 * the memo and the cap flag. It reads no ledger and writes nothing.
 *
 * - **One pass at a time.** A caller that needs evidence while a pass is in
 *   flight awaits it and takes its answers, final or not, for the pairs it
 *   covered; afterwards it starts a pass of its own only for pairs that no
 *   pass covered and the memo does not hold. Within a pass, E1 is one call
 *   for all its pairs and the E2 calls run one after another, so at most one
 *   call is in flight at any instant. A call that rejects (an RPC error) ends
 *   the pass: every caller waiting on it rejects with that error, and nothing
 *   of that pass is memoised.
 * - **The cap.** Each pass races `capMs`. When it fires, every caller
 *   waiting on the pass gets `"capped"`, the cap flag is set, and no pass
 *   starts for the rest of the session. The one call then in flight cannot
 *   be aborted; its answer is ignored (not memoised, not returned), and no
 *   E1 or E2 call starts from it.
 * - **The memo**, keyed by (record key, `txSignature`), holds final answers
 *   only: E1 `finalized` with `err`, or E1 `finalized` and successful with an
 *   agreeing E2 whose body is readable. Everything else (null, `processed`
 *   or `confirmed` with or without `err`, E2 null or disagreeing, an E2 body
 *   that is not readable (R86), a malformed answer, an error, the cap) is
 *   read again at the next pass. The memo keeps the raw evidence, so
 *   `appliedBy` judges it against the record at every pass.
 */
export class EarlierReads {
  private readonly cfg: EarlierReadsConfig;
  private inFlight?: Pass;
  private capped = false;
  private readonly memo = new Map<string, EarlierEvidence>();

  constructor(cfg: EarlierReadsConfig) {
    this.cfg = cfg;
  }

  /** True once a pass hit the cap; no pass starts after that. */
  get isCapped(): boolean {
    return this.capped;
  }
  /** Number of memoised (record key, `txSignature`) pairs; for tests. */
  get memoSize(): number {
    return this.memo.size;
  }

  /**
   * The evidence for each pair, by record key, or `"capped"` when a pass
   * this caller waited on hit the cap, or when the session is capped and a
   * pair is not memoised. Rejects when the pass it waited on rejected.
   */
  async read(pairs: readonly EarlierPair[]): Promise<Map<string, EarlierEvidence> | 'capped'> {
    const want = new Map<string, EarlierPair>();
    for (const p of pairs) want.set(pairId(p), { key: p.key, txSignature: p.txSignature });
    const got = new Map<string, EarlierEvidence>();
    for (;;) {
      for (const id of want.keys()) {
        if (got.has(id)) continue;
        const m = this.memo.get(id);
        if (m) got.set(id, m);
      }
      if (got.size === want.size) break;
      if (this.capped) return 'capped';
      const pass = this.inFlight ?? this.startPass([...want].filter(([id]) => !got.has(id)).map(([, p]) => p));
      const answers = await pass.result;
      if (answers === 'capped') return 'capped';
      for (const id of want.keys()) {
        const a = answers.get(id);
        if (a && !got.has(id)) got.set(id, a);
      }
    }
    const out = new Map<string, EarlierEvidence>();
    for (const [id, p] of want) out.set(p.key, got.get(id)!);
    return out;
  }

  private startPass(pairs: EarlierPair[]): Pass {
    const pass: Pass = { dead: false, result: undefined as unknown as Pass['result'] };
    let timer: ReturnType<typeof setTimeout> | undefined;
    const cap = new Promise<'capped'>((resolve) => {
      timer = setTimeout(() => {
        pass.dead = true;
        this.capped = true;
        resolve('capped');
      }, this.cfg.capMs);
    });
    const work = this.runPass(pass, pairs);
    // An answer or error that arrives after the cap is ignored.
    work.catch(() => undefined);
    pass.result = Promise.race([work, cap]).finally(() => {
      clearTimeout(timer);
      pass.dead = true;
      if (this.inFlight === pass) this.inFlight = undefined;
    });
    this.inFlight = pass;
    return pass;
  }

  private async runPass(pass: Pass, pairs: EarlierPair[]): Promise<Map<string, EarlierEvidence>> {
    const signatures = [...new Set(pairs.map((p) => p.txSignature))];
    const answer: unknown = await this.cfg.statuses(signatures);
    if (pass.dead) return new Map();
    const wellFormed = Array.isArray(answer) && answer.length === signatures.length;
    const statusOf = new Map<string, SignatureStatusJson | null>();
    signatures.forEach((s, i) => {
      const entry: unknown = wellFormed ? (answer as unknown[])[i] : null;
      statusOf.set(s, entry && typeof entry === 'object' ? (entry as SignatureStatusJson) : null);
    });
    const out = new Map<string, EarlierEvidence>();
    const final: [string, EarlierEvidence][] = [];
    const txOf = new Map<string, TransactionJson | null>();
    for (const p of pairs) {
      const status = statusOf.get(p.txSignature) ?? null;
      const evidence: EarlierEvidence = { txSignature: p.txSignature, status };
      const e1 = judgeE1(status);
      if (e1.kind === 'failure') final.push([pairId(p), evidence]);
      if (e1.kind === 'success') {
        if (!txOf.has(p.txSignature)) {
          const tx = await this.cfg.transaction(p.txSignature);
          if (pass.dead) return new Map();
          txOf.set(p.txSignature, tx ?? null);
        }
        evidence.transaction = txOf.get(p.txSignature)!;
        if (judgeE2(evidence.transaction, p.txSignature, e1.slot) === 'agree') final.push([pairId(p), evidence]);
      }
      out.set(pairId(p), evidence);
    }
    for (const [id, evidence] of final) this.memo.set(id, evidence);
    return out;
  }
}
